/** Smart-guide snap math for Tier 2 Group D. Pure functions — no
 *  DOM, no canvas, no Fabric. Snap a dragged layer's bounding box
 *  to OTHER LAYERS' bounding-box edges and centers so position
 *  alignment with existing content is effortless. Pairs with the
 *  existing snap-to-guides / snap-to-grid in `lib/snap.ts` (which
 *  matches against user-placed lines and grid intersections); this
 *  one matches against the actual content already on the canvas.
 *
 *  The dragged bbox contributes 3 candidate X positions
 *  (left edge, center, right edge) and 3 candidate Y positions
 *  (top, center, bottom). Each target layer's bbox contributes
 *  the same 3+3 lines. We snap when any dragged candidate lands
 *  within tolerance of any target candidate on the matching axis.
 *  Each axis is computed independently — a drag near a target can
 *  align on X, Y, or both.
 *
 *  Two metadata pieces are returned for the indicator overlay:
 *    - `matchX` / `matchY`: which target layer was matched on each
 *      axis + which target edge + the snap value. The overlay
 *      draws a magenta line from the dragged layer to the target
 *      along the matching axis.
 *    - `distance`: scene-space gap between the matched edges,
 *      surfaced as a small label so the user can see "this layer
 *      is 32 px from that one" at a glance.
 *
 *  Tolerance is scene-space (callers convert from screen pixels
 *  via the current zoom). The dragged layer's id is excluded from
 *  the target list — a layer can't snap to itself. */

export interface LayerBBox {
  /** Layer id — used to filter the dragged layer out of target
   *  candidates AND surfaced in the returned match metadata so
   *  the indicator overlay can draw a line to THIS specific
   *  layer. */
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export type SnapEdge = 'left' | 'center-x' | 'right' | 'top' | 'center-y' | 'bottom';

export interface SmartSnapMatch {
  /** Id of the target layer the dragged bbox snapped to on this
   *  axis. */
  targetId: string;
  /** Which edge of the dragged layer is now aligned with the
   *  target. */
  sourceEdge: SnapEdge;
  /** Which edge of the target layer the dragged edge aligned to. */
  targetEdge: SnapEdge;
  /** Scene-space coordinate of the aligned position on this axis
   *  (X coord for X-axis matches; Y coord for Y-axis matches). */
  position: number;
}

export interface SmartSnapOptions {
  /** Scene-space distance within which a candidate line "pulls"
   *  the dragged bbox. 0 = no snap. Callers using screen-pixel
   *  tolerance should divide by current zoom. */
  tolerance: number;
  /** Bbox of the layer being dragged. Used to compute the 3+3
   *  source candidates. */
  source: LayerBBox;
  /** Bboxes of every other visible non-overlay layer on the
   *  canvas. The source layer is filtered out by id internally. */
  targets: LayerBBox[];
}

export interface SmartSnapResult {
  /** Snapped top-left of the dragged bbox. Equals
   *  `source.{left,top}` when no snap fired on that axis. */
  left: number;
  top: number;
  /** Match metadata per axis. Null when nothing was within
   *  tolerance. Consumed by `CanvasOverlays/SmartGuides` to
   *  render magenta lines + distance labels. */
  matchX: SmartSnapMatch | null;
  matchY: SmartSnapMatch | null;
}

/** X-axis edge candidates from a bbox. Same shape used for the
 *  dragged source (where `offset` is how much to add to a snap
 *  target to put the bbox's left at the right place) and for
 *  each target (where `offset` is unused — only `value` matters). */
interface AxisCandidate {
  edge: SnapEdge;
  value: number;
}

function xCandidates(b: LayerBBox): AxisCandidate[] {
  return [
    { edge: 'left', value: b.left },
    { edge: 'center-x', value: b.left + b.width / 2 },
    { edge: 'right', value: b.left + b.width },
  ];
}

function yCandidates(b: LayerBBox): AxisCandidate[] {
  return [
    { edge: 'top', value: b.top },
    { edge: 'center-y', value: b.top + b.height / 2 },
    { edge: 'bottom', value: b.top + b.height },
  ];
}

/** Snap a dragged bbox to the nearest other-layer edge/center on
 *  each axis. Returns the new top-left + match metadata. */
export function smartSnap(opts: SmartSnapOptions): SmartSnapResult {
  if (opts.tolerance <= 0 || opts.targets.length === 0) {
    return { left: opts.source.left, top: opts.source.top, matchX: null, matchY: null };
  }
  // Exclude the source layer's own bbox from target candidates —
  // a layer can't snap to itself.
  const eligibleTargets = opts.targets.filter((t) => t.id !== opts.source.id);
  if (eligibleTargets.length === 0) {
    return { left: opts.source.left, top: opts.source.top, matchX: null, matchY: null };
  }

  const srcXCands = xCandidates(opts.source);
  const srcYCands = yCandidates(opts.source);

  // For each axis, walk every (source-candidate × target × target-
  // candidate) combination and keep the lowest-distance match
  // within tolerance. The brute-force walk is 3 * N * 3 = 9N per
  // axis per drag tick; with the per-drag-start target snapshot
  // (so N is small + stable) this is microsecond-level even on
  // documents with 100+ layers.
  interface BestMatch {
    dist: number;
    sourceEdge: SnapEdge;
    targetId: string;
    targetEdge: SnapEdge;
    sourceValue: number;
    targetValue: number;
  }
  let bestX: BestMatch | null = null;
  let bestY: BestMatch | null = null;

  for (const target of eligibleTargets) {
    for (const sc of srcXCands) {
      for (const tc of xCandidates(target)) {
        const dist = Math.abs(sc.value - tc.value);
        if (dist > opts.tolerance) continue;
        if (!bestX || dist < bestX.dist) {
          bestX = {
            dist,
            sourceEdge: sc.edge,
            targetId: target.id,
            targetEdge: tc.edge,
            sourceValue: sc.value,
            targetValue: tc.value,
          };
        }
      }
    }
    for (const sc of srcYCands) {
      for (const tc of yCandidates(target)) {
        const dist = Math.abs(sc.value - tc.value);
        if (dist > opts.tolerance) continue;
        if (!bestY || dist < bestY.dist) {
          bestY = {
            dist,
            sourceEdge: sc.edge,
            targetId: target.id,
            targetEdge: tc.edge,
            sourceValue: sc.value,
            targetValue: tc.value,
          };
        }
      }
    }
  }

  // Translate the matched source candidate so it lands on the
  // target line. Delta = target - source for the matched
  // candidate; apply to the bbox's left/top.
  let newLeft = opts.source.left;
  let matchX: SmartSnapMatch | null = null;
  if (bestX) {
    newLeft = opts.source.left + (bestX.targetValue - bestX.sourceValue);
    matchX = {
      targetId: bestX.targetId,
      sourceEdge: bestX.sourceEdge,
      targetEdge: bestX.targetEdge,
      position: bestX.targetValue,
    };
  }
  let newTop = opts.source.top;
  let matchY: SmartSnapMatch | null = null;
  if (bestY) {
    newTop = opts.source.top + (bestY.targetValue - bestY.sourceValue);
    matchY = {
      targetId: bestY.targetId,
      sourceEdge: bestY.sourceEdge,
      targetEdge: bestY.targetEdge,
      position: bestY.targetValue,
    };
  }

  return { left: newLeft, top: newTop, matchX, matchY };
}

/** Compute the scene-space distance between two layer bboxes
 *  along ONE axis, for the label shown next to a smart-guide
 *  match. When the two bboxes overlap on the axis, the distance
 *  is 0; otherwise it's the gap between the nearest edges.
 *
 *  Used by the indicator overlay AFTER `smartSnap` matches —
 *  the caller passes the dragged bbox's POST-SNAP position and
 *  the target layer's bbox. */
export function edgeToEdgeDistance(axis: 'x' | 'y', a: LayerBBox, b: LayerBBox): number {
  if (axis === 'x') {
    const aLeft = a.left;
    const aRight = a.left + a.width;
    const bLeft = b.left;
    const bRight = b.left + b.width;
    if (aRight < bLeft) return bLeft - aRight; // a is left of b
    if (bRight < aLeft) return aLeft - bRight; // b is left of a
    return 0; // overlap
  }
  const aTop = a.top;
  const aBottom = a.top + a.height;
  const bTop = b.top;
  const bBottom = b.top + b.height;
  if (aBottom < bTop) return bTop - aBottom;
  if (bBottom < aTop) return aTop - bBottom;
  return 0;
}
